import axios from 'axios';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { AsyncTaskType } from '../../types/async-task/async-task.type';
import { PaginationType } from '../../types/utils/pagination.type';
import { RouteParamsType } from '../../types/utils/router-params.type';
import { additionalQueryParams } from '../../utils/utilities';
import { setShowModal, setModalContent } from '../utils/utils.slice';
import {
  CreateLocationDto,
  LocationDetailsType,
  LocationListItemType,
  UpdateLocationDto,
} from '../../types/location/location.type';
import {
  CreateLocationPanelInfoDto,
  LocationPanelInfoType,
  UpdateLocationPanelInfoDto,
} from '../../types/location/location-panel-info/location-panel-info.type';
import { PBXListItemType } from '../../types/pbx/PBXListItem.type';
import { DIDsListItemType } from '../../types/did/did.type';
import { PbxExtensionListItemType } from '../../types/pbx-extension/pbx-extension.type';
import {
  LocationSiteReadinessDetailsType,
  SiteReadinessType,
  UpdateSiteReadinessDto,
} from '../../types/location/site-readiness/site-readiness.type';
import { rejectWithApiError } from '../../utils/api';
import { RootReducerState } from '../../redux/Reducers';

export {
  getLocationTicketsTabList,
  getLocationOrdersTabList,
  getLocationServiceCallsTabList,
} from './location-tab-list.thunks';

export const postLocations = createAsyncThunk<
  AsyncTaskType,
  { data: { organization: { id: number }; addresses: Array<any> } },
  { state: RootReducerState }
>('locations/postLocations', async ({ data }, thunkAPI) => {
  try {
    const response = await axios.post<AsyncTaskType>(`${process.env.REACT_APP_API_URL}/location/bulk`, data);
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during creating locations!' });
  }
});

export const postLocationDetails = createAsyncThunk<
  LocationDetailsType,
  { data: CreateLocationDto },
  { state: RootReducerState }
>('locations/postLocationDetails', async ({ data }, thunkAPI) => {
  try {
    const response = await axios.post<LocationDetailsType>(`${process.env.REACT_APP_API_URL}/location`, data);
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during creating location!' });
  }
});

export const getLocationsList = createAsyncThunk<
  PaginationType<LocationListItemType>,
  RouteParamsType & { controller?: AbortController },
  { state: RootReducerState }
>('locations/getLocationsList/no-loader', async (data, thunkAPI) => {
  try {
    const { controller, ...params } = data;
    const additionalParams = additionalQueryParams(
      thunkAPI.getState().sharedReducer.selectedCompany,
      null,
      thunkAPI.getState().sharedReducer.includeSuborgs,
      thunkAPI.getState().sharedReducer.selectedCountry
    );
    const mergedParams = { ...params, ...additionalParams };
    const queryString = Object.keys(mergedParams)
      .map(key => key + '=' + mergedParams[key])
      .join('&');

    const response = await axios.get<PaginationType<LocationListItemType>>(
      `${process.env.REACT_APP_API_URL}/location${queryString ? `?${queryString}` : ''}`,
      controller ? { signal: controller.signal } : undefined
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching locations!' });
  }
});

export const getLocationDetails = createAsyncThunk<LocationDetailsType, { id: number }, { state: RootReducerState }>(
  'locations/getLocationDetails',
  async ({ id }, thunkAPI) => {
    try {
      const response = await axios.get<LocationDetailsType>(`${process.env.REACT_APP_API_URL}/location/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching location!' });
    }
  }
);

export const patchLocationDetails = createAsyncThunk<
  LocationDetailsType,
  { id: number; data: UpdateLocationDto },
  { state: RootReducerState }
>('locations/patchLocationDetails', async ({ data, id }, thunkAPI) => {
  try {
    const response = await axios.patch<LocationDetailsType>(`${process.env.REACT_APP_API_URL}/location/${id}`, data);
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during updating location!' });
  }
});

export const patchLocationOrganizationDetails = createAsyncThunk<
  LocationDetailsType,
  { id: number; data: { organization: { id: number } } },
  { state: RootReducerState }
>('locations/patchLocationOrganizationDetails', async ({ data, id }, thunkAPI) => {
  try {
    const response = await axios.patch<LocationDetailsType>(
      `${process.env.REACT_APP_API_URL}/location/${id}/change-org`,
      data
    );

    thunkAPI.dispatch(getLocationDetails({ id }));

    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during updating location!' });
  }
});

export const deleteLocationDetails = createAsyncThunk<
  LocationDetailsType,
  { id: number; fetchList: boolean },
  { state: RootReducerState }
>('locations/deleteLocationDetails', async ({ id, fetchList }, thunkAPI) => {
  try {
    const response = await axios.delete<LocationDetailsType>(`${process.env.REACT_APP_API_URL}/location/${id}`);
    if (fetchList) thunkAPI.dispatch(getLocationsList(thunkAPI.getState().locationsReducer.locationsRouteParams));
    thunkAPI.dispatch(setShowModal(false));
    thunkAPI.dispatch(setModalContent(null));
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during deleting location!' });
  }
});

export const getLocationTelcoList = createAsyncThunk<
  {
    pbxs: Array<PBXListItemType>;
    dids: Array<DIDsListItemType>;
    pbxExtensions: Array<PbxExtensionListItemType>;
  },
  { id: number },
  { state: RootReducerState }
>('locations/getLocationTelcoList', async ({ id }, thunkAPI) => {
  try {
    const response = await axios.get<{
      pbxs: Array<PBXListItemType>;
      dids: Array<DIDsListItemType>;
      pbxExtensions: Array<PbxExtensionListItemType>;
    }>(`${process.env.REACT_APP_API_URL}/location/${id}/telco`);
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching telco information!' });
  }
});

export const getLocationServicesList = createAsyncThunk<any, { id: number }, { state: RootReducerState }>(
  'locations/getLocationServicesList',
  async ({ id }, thunkAPI) => {
    try {
      const response = await axios.get<any>(`${process.env.REACT_APP_API_URL}/location/${id}/services`);
      return response.data?.services || [];
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching services information!' });
    }
  }
);

export const getLocationEquipmentList = createAsyncThunk<any, { id: number }, { state: RootReducerState }>(
  'locations/getLocationEquipmentList',
  async ({ id }, thunkAPI) => {
    try {
      const response = await axios.get<any>(`${process.env.REACT_APP_API_URL}/location/${id}/equipment`);
      return response.data || [];
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching equipment information!' });
    }
  }
);

export const getLocationConnectivityList = createAsyncThunk<any, { id: number }, { state: RootReducerState }>(
  'locations/getLocationConnectivityList',
  async ({ id }, thunkAPI) => {
    try {
      const response = await axios.get<any>(`${process.env.REACT_APP_API_URL}/location/${id}/connectivity`);
      return response.data || [];
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching connectivity information!' });
    }
  }
);

export const getLocationTicketsList = createAsyncThunk<any, { id: number }, { state: RootReducerState }>(
  'locations/getLocationTicketsList',
  async ({ id }, thunkAPI) => {
    try {
      const response = await axios.get<any>(`${process.env.REACT_APP_API_URL}/location/${id}/tickets`);
      return response.data?.tickets || [];
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching tickets information!' });
    }
  }
);

export const getLocationServiceCallsList = createAsyncThunk<any, { id: number }, { state: RootReducerState }>(
  'locations/getLocationServiceCallsList',
  async ({ id }, thunkAPI) => {
    try {
      const response = await axios.get<any>(`${process.env.REACT_APP_API_URL}/service-ticket?locationId=${id}`);
      return response.data?.items || [];
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching service calls information!' });
    }
  }
);

export const getLocationServiceCallAttachments = createAsyncThunk<any, { id: number }, { state: RootReducerState }>(
  'locations/getLocationServiceCallAttachments',
  async ({ id }, thunkAPI) => {
    try {
      const response = await axios.get<any>(`${process.env.REACT_APP_API_URL}/location/${id}/service-call-attachments`);
      return response.data?.attachments || [];
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching service call attachments!' });
    }
  }
);

export const getLocationOrdersList = createAsyncThunk<any, { id: number }, { state: RootReducerState }>(
  'locations/getLocationOrdersList',
  async ({ id }, thunkAPI) => {
    try {
      const response = await axios.get<any>(`${process.env.REACT_APP_API_URL}/location/${id}/orders`);
      return response.data?.orders || [];
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching orders information!' });
    }
  }
);

export const getLocationSiteReadinessDetails = createAsyncThunk<
  LocationSiteReadinessDetailsType,
  { id: number },
  { state: RootReducerState }
>('locations/getLocationSiteReadinessDetails', async ({ id }, thunkAPI) => {
  try {
    const response = await axios.get<LocationSiteReadinessDetailsType>(
      `${process.env.REACT_APP_API_URL}/location/${id}`
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching site readiness!' });
  }
});

export const saveLocationSiteReadiness = createAsyncThunk<
  SiteReadinessType,
  { locationId: number; siteReadinessId?: number | null; data: UpdateSiteReadinessDto },
  { state: RootReducerState }
>('locations/saveLocationSiteReadiness', async ({ locationId, siteReadinessId, data }, thunkAPI) => {
  try {
    const response = siteReadinessId
      ? await axios.patch<SiteReadinessType>(
          `${process.env.REACT_APP_API_URL}/location/site-readiness/${siteReadinessId}`,
          data
        )
      : await axios.post<SiteReadinessType>(`${process.env.REACT_APP_API_URL}/location/site-readiness`, {
          ...data,
          location: { id: locationId },
        });
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during saving site readiness!' });
  }
});

export const getImageURL = createAsyncThunk<
  string,
  { lat: string | number; lon: string | number },
  { state: RootReducerState }
>('locations/getImageURL', async ({ lat, lon }, thunkAPI) => {
  try {
    const url = `https://maps.googleapis.com/maps/api/streetview?size=650x300&source=outdoor&location=${lat},${lon}&fov=120&key=AIzaSyBtIpQL1xqZAhGl0QKqHyr-5AF3ywj7fRg`;
    const instance = axios.create();
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    delete instance.defaults.headers.common;
    await instance.get(url);
    return url;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching image!' });
  }
});

export const postLocationInfoPanelDetails = createAsyncThunk<
  LocationPanelInfoType,
  { data: CreateLocationPanelInfoDto; id: number; type: string },
  { state: RootReducerState }
>('locations/postLocationInfoPanelDetails', async ({ data, id, type }, thunkAPI) => {
  try {
    const response = await axios.patch<LocationPanelInfoType>(
      `${process.env.REACT_APP_API_URL}/location/${id}/locationInfo?type=${type}`,
      data
    );
    thunkAPI.dispatch(getLocationDetails({ id }));
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during creating location panel info!' });
  }
});

export const patchLocationInfoPanelDetails = createAsyncThunk<
  LocationPanelInfoType,
  { id: number; data: UpdateLocationPanelInfoDto; locationInfoID: number },
  { state: RootReducerState }
>('locations/patchLocationInfoDetails', async ({ data, id, locationInfoID }, thunkAPI) => {
  try {
    const response = await axios.patch<LocationPanelInfoType>(
      `${process.env.REACT_APP_API_URL}/location/${id}/locationInfo/${locationInfoID}`,
      data
    );
    thunkAPI.dispatch(getLocationDetails({ id }));
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during updating location panel info!' });
  }
});

export const getPublicLocationDetails = createAsyncThunk<
  LocationDetailsType,
  { hash: string },
  { state: RootReducerState }
>('locations/getPublicLocationDetails', async ({ hash }, thunkAPI) => {
  try {
    const response = await axios.get<LocationDetailsType>(`${process.env.REACT_APP_API_URL}/location/public/${hash}`);
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching location!' });
  }
});
